/**
 * CONTEXT: Ball Input Context
 *
 * RESPONSIBILITY:
 * - Hold current delivery input only
 *
 * MUST NEVER:
 * - Hold backend-derived state
 * - Persist across saves
 */

import { createContext, useContext, useState } from "react";

const BallInputContext = createContext(null);

export function BallInputProvider({ children }) {
  const [ball, setBall] = useState({
    striker: null,
    non_striker: null,
    bowler: null,
    completed_runs: 0,

    is_wide: false,
    is_no_ball: false,
    is_bye: false,
    is_leg_bye: false,
    is_boundary: false,
    is_short_run: false,
    is_quick_running: false,
    is_free_hit: false,
    no_ball_type: "",

    has_wicket: false,
    wicket_type: null,
    dismissed_player_id: null,
    dismissed_by_id: null,
    decision_given_by_umpire : null,
    caught_by_id: null,
    stumped_by_id: null,
    run_out_fielder_1_id: null,
    run_out_fielder_2_id: null,
    run_out_subtype: "",
    batters_crossed: null,
    is_dead_ball: false,
    dead_ball_reason: "",
    appealed_for: "",

    analytics_enabled: true,
    analytics: {
      delivery_type: "",
      foot_movement: "",
      air_movement: "",
      control: "",
      shot_connection: "",
      // Tier 1
      stroke: "",
      bat_subject: "",
      shot_intent: "",
      outcome_quality: "",
      // Tier 2
      batsman_activity: "",
      keeper_activity: "",
      bowler_plan: "",
      // Tier 3
      spin_direction: "",
      drift_direction: "",
      turn_after_pitch: "",
      bounce_category: "",
    },

    spatial: {
      shot_zone_x: null,
      shot_zone_y: null,
      wagon_wheel_x: null,
      wagon_wheel_y: null,
      pitch_zone: null,
      stump_zone: null,
      height_zone: null,
      batter_stump_zone: null,
      // Impact-on-batsman grid
      batter_impact_height: "",
      batter_impact_line: "",
      batter_impact_x: null,
      batter_impact_y: null,
      structured_region_id: null,
      structured_slice_index: null,
      structured_band_index: null,
      structured_position: null,
    },
    fielding: {
      fielders: [
        {
          player: null,
          action: "",
          fielding_position: "",
          runs_saved: null,
          runs_misfielded: null,
          overthrow_runs: null,
          difficulty: null,
        },
      ],
    },
    drs: {
      is_appealed: false,
      review_team_side: "",
      review_team_id: null,
      on_field_decision: "",
      overruled: false,
      final_decision: "",
      decision_given_by_umpire: null,
      third_umpire: null,
      // Tier 2 — modern DRS context
      impact_zone: "",
      wicket_impact: "",
      soft_signal: "",
    },

    release: {
      bowler_release_x: null,
      bowler_release_y: null,
      bowler_release_position: "Over",
      wicket_keeper_position: "WK UP",
      ball_type: "",
      is_break: false,
      break_type: "",
      // Tier 1 — speed gun
      speed_kph: null,
    },
    trajectory: {
      x: null,
      y: null,
    },

    video: {
      video_source_id: "obs-main",
      video_start_ms: null,
      video_end_ms: null,
      camera_angle: "",
    },
  });

  return (
    <BallInputContext.Provider value={{ ball, setBall }}>
      {children}
    </BallInputContext.Provider>
  );
}

export function useBallInput() {
  return useContext(BallInputContext);
}
